import type { Rarity } from '@/types/rewards';

// ── Rarity display tokens ───────────────────────────────────

export const RARITY_CONFIG: Record<
  Rarity,
  {
    label: string;
    color: string;
    bg: string;
    border: string;
    glow: string;
  }
> = {
  common: {
    label: 'Common',
    color: '#8A7F72',
    bg: '#F2EBDD',
    border: '#E3D5B8',
    glow: 'rgba(138,127,114,0.18)',
  },
  uncommon: {
    label: 'Uncommon',
    color: '#3F8E4A',
    bg: '#E7F2E2',
    border: '#BFD8B8',
    glow: 'rgba(63,142,74,0.20)',
  },
  rare: {
    label: 'Rare',
    color: '#1F6A8A',
    bg: '#DEEBF3',
    border: '#A9C9DB',
    glow: 'rgba(31,106,138,0.25)',
  },
  epic: {
    label: 'Epic',
    color: '#7842B0',
    bg: '#EBE0F4',
    border: '#C8B0E0',
    glow: 'rgba(120,66,176,0.30)',
  },
  legendary: {
    label: 'Legendary',
    color: '#B85A1E',
    bg: '#FAE1C5',
    border: '#E8B679',
    glow: 'rgba(184,90,30,0.32)',
  },
};

// ── Species metadata (client-side reference) ────────────────

export interface SpeciesInfo {
  id: string;
  name: string;
  rarity: Rarity;
  tagline: string;
  lore: string;
  palette: { body: string; belly: string; ears: string; accent: string; mark: string };
}

export const SPECIES = {
  glimmoth: {
    id: 'glimmoth',
    name: 'Glimmoth',
    rarity: 'common',
    tagline: 'A moth-fox who naps inside paper lanterns.',
    lore: 'Glimmoths drift between bedside lamps, leaving tiny crescent footprints in the dust. Said to bookmark your page if you fall asleep reading.',
    palette: {
      body: '#E8B27A',
      belly: '#F8DDB8',
      ears: '#C97F4F',
      accent: '#F0DDF6',
      mark: '#7C4FA1',
    },
  },
  pipperel: {
    id: 'pipperel',
    name: 'Pipperel',
    rarity: 'common',
    tagline: 'Bird and frog rolled into one bouncy bell.',
    lore: 'Pipperels hum the last song they overheard. Surprisingly good at carrying a tune; bad at remembering where they put their pebbles.',
    palette: {
      body: '#9CC9C0',
      belly: '#E5F2EE',
      ears: '#F2C56B',
      accent: '#5E978E',
      mark: '#FFFCEC',
    },
  },
  quilltail: {
    id: 'quilltail',
    name: 'Quilltail',
    rarity: 'uncommon',
    tagline: 'A scholarly apple-hog with reading-spectacle eyes.',
    lore: "Born wearing tiny round rims around its eyes — no one knows why. Quilltails sort their quills by length and won't be rushed.",
    palette: {
      body: '#D75C4E',
      belly: '#F4D3A6',
      ears: '#8E2E26',
      accent: '#3D2A18',
      mark: '#F8E9C5',
    },
  },
  bumblebloom: {
    id: 'bumblebloom',
    name: 'Bumblebloom',
    rarity: 'uncommon',
    tagline: 'Half bumble, half bunny — sneezes pollen.',
    lore: 'Bumblebloom freckles glow brightest in spring. They live in flower-cup beds and trade pollen for kind words.',
    palette: {
      body: '#F5C84B',
      belly: '#FFF1C4',
      ears: '#E8A6C9',
      accent: '#3A2A1A',
      mark: '#FFFFFF',
    },
  },
  wispwhal: {
    id: 'wispwhal',
    name: 'Wispwhal',
    rarity: 'rare',
    tagline: 'A cloud-whale who rains a little when happy.',
    lore: 'Wispwhals drift through high-up rooms looking for thoughtful kids. Their tail-puff turns blue before it sprinkles.',
    palette: {
      body: '#C7DCEC',
      belly: '#EAF3FA',
      ears: '#7FAACE',
      accent: '#3D5C7A',
      mark: '#FFFFFF',
    },
  },
  embergrub: {
    id: 'embergrub',
    name: 'Embergrub',
    rarity: 'rare',
    tagline: 'A tiny dragon-caterpillar with glowing freckles.',
    lore: 'Embergrubs are warm to the touch but never burn. Each spot pulses to a different beat, like a constellation that learned rhythm.',
    palette: {
      body: '#3F2A4F',
      belly: '#6E4988',
      ears: '#FFB347',
      accent: '#FFE07A',
      mark: '#FFD13B',
    },
  },
  lumibrook: {
    id: 'lumibrook',
    name: 'Lumibrook',
    rarity: 'epic',
    tagline: 'A jelly-deer whose antlers light the room.',
    lore: 'Lumibrooks float a hand-span off the floor, antlers glowing the color of whatever feeling is nearby. They prefer libraries.',
    palette: {
      body: '#B49EE0',
      belly: '#E5DBF7',
      ears: '#7B5FBE',
      accent: '#FFD8FA',
      mark: '#FFF8C2',
    },
  },
  starmole: {
    id: 'starmole',
    name: 'Starmole',
    rarity: 'legendary',
    tagline: 'A velvet burrower whose fur holds real constellations.',
    lore: 'Only one Starmole has been seen at a time, anywhere, ever. Their prism-noses split lamplight into miniature aurorae.',
    palette: {
      body: '#1B1B3A',
      belly: '#3D3672',
      ears: '#7A6FD8',
      accent: '#FFE066',
      mark: '#FFFFFF',
    },
  },
} satisfies Record<string, SpeciesInfo>;

export const ALL_SPECIES_IDS = Object.keys(SPECIES) as Array<keyof typeof SPECIES>;
export type SpeciesId = keyof typeof SPECIES;
export const TOTAL_SPECIES = ALL_SPECIES_IDS.length; // 8

export function isSpeciesId(speciesId: string): speciesId is SpeciesId {
  return Object.prototype.hasOwnProperty.call(SPECIES, speciesId);
}

export function getSpecies(speciesId: string): SpeciesInfo | null {
  return isSpeciesId(speciesId) ? SPECIES[speciesId] : null;
}

// ── Egg tier display helpers ────────────────────────────────

/** The rarity with the highest weight in a tier's pool — used for the "Mostly X" pill. */
export function dominantRarity(pool: Record<Rarity, number>): Rarity {
  let best: Rarity = 'common';
  let bestWeight = 0;
  for (const [r, w] of Object.entries(pool) as [Rarity, number][]) {
    if (w > bestWeight) {
      best = r;
      bestWeight = w;
    }
  }
  return best;
}

/** Format hatch duration for display. */
export function formatHatchTime(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainMins = minutes % 60;
  return remainMins > 0 ? `${hours}h ${remainMins}m` : `${hours}h`;
}

/** Format countdown remaining for an incubating egg. */
export function formatCountdown(remainingMs: number): string {
  if (remainingMs <= 0) return 'Ready!';
  const totalSec = Math.ceil(remainingMs / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min >= 60) {
    const hr = Math.floor(min / 60);
    const remMin = min % 60;
    return `${hr}:${String(remMin).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  }
  return `${min}:${String(sec).padStart(2, '0')}`;
}
